import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'

/**
 * WikiSettingsPanel - Configure wiki auto-update behavior
 * 
 * Allows team managers to:
 * - Enable/disable auto-apply for AI updates
 * - Set confidence threshold for auto-application
 * - Configure protection period for manually edited items
 */
function WikiSettingsPanel({ teamId }) {
  const [settings, setSettings] = useState({
    auto_apply_enabled: true,
    confidence_threshold: 0.80,
    protect_manual_edits_days: 30
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [stats, setStats] = useState({ autoApplied: 0, queued: 0 })

  useEffect(() => {
    if (teamId) {
      fetchSettings()
      fetchStats()
    }
  }, [teamId])

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase.rpc('get_wiki_settings', {
        p_team_id: teamId
      })

      if (error) throw error

      if (data) {
        setSettings({
          auto_apply_enabled: data.auto_apply_enabled ?? true,
          confidence_threshold: data.confidence_threshold ?? 0.80,
          protect_manual_edits_days: data.protect_manual_edits_days ?? 30
        })
      }
    } catch (err) {
      console.error('Failed to fetch wiki settings:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      // Get count of auto-applied changes in last 30 days
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      
      const [autoApplied, queued] = await Promise.all([
        supabase
          .from('wiki_change_log')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', teamId)
          .eq('auto_applied', true)
          .is('undone_at', null)
          .gte('created_at', thirtyDaysAgo),
        supabase
          .from('wiki_suggestions')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', teamId)
          .eq('status', 'pending')
      ])

      setStats({
        autoApplied: autoApplied.count || 0,
        queued: queued.count || 0
      })
    } catch (err) {
      console.error('Failed to fetch wiki stats:', err)
    }
  }

  const updateSettings = async (updates) => {
    setSaving(true)
    try {
      const { data, error } = await supabase.rpc('update_wiki_settings', {
        p_team_id: teamId,
        p_auto_apply_enabled: updates.auto_apply_enabled ?? settings.auto_apply_enabled,
        p_confidence_threshold: updates.confidence_threshold ?? settings.confidence_threshold,
        p_protect_manual_edits_days: updates.protect_manual_edits_days ?? settings.protect_manual_edits_days
      })

      if (error) throw error

      setSettings(prev => ({ ...prev, ...updates }))
      toast.success('Wiki settings saved')
    } catch (err) {
      console.error('Failed to update wiki settings:', err)
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8 text-brand-slate">
          Loading wiki settings...
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <h2 className="font-heading text-xl font-bold mb-2">Wiki Auto-Update Settings</h2>
      <p className="text-sm text-brand-slate mb-6">
        Control how AI automatically updates your team wiki from dispatches
      </p>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-brand-cream rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-brand-green">{stats.autoApplied}</div>
          <div className="text-sm text-brand-slate">Auto-applied (30d)</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-amber-600">{stats.queued}</div>
          <div className="text-sm text-brand-slate">Pending review</div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Auto-apply toggle */}
        <div className="flex items-center justify-between p-4 border border-border rounded-lg">
          <div>
            <p className="font-medium text-brand-charcoal">Auto-apply AI updates</p>
            <p className="text-sm text-brand-slate mt-1">
              Automatically add new wiki items from dispatches without manual review
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.auto_apply_enabled}
              onChange={(e) => updateSettings({ auto_apply_enabled: e.target.checked })}
              disabled={saving}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-green/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-green"></div>
          </label>
        </div>

        {/* Confidence threshold */}
        <div className="p-4 border border-border rounded-lg">
          <label className="font-medium text-brand-charcoal block mb-2">
            Confidence threshold
          </label>
          <p className="text-sm text-brand-slate mb-3">
            Only auto-apply items above this confidence level. Lower values = more automatic updates.
          </p>
          <select
            value={settings.confidence_threshold}
            onChange={(e) => updateSettings({ confidence_threshold: parseFloat(e.target.value) })}
            disabled={saving || !settings.auto_apply_enabled}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value={0.95}>Very High (95%+) - Most conservative</option>
            <option value={0.90}>High (90%+) - Conservative</option>
            <option value={0.80}>Medium (80%+) - Recommended</option>
            <option value={0.70}>Low (70%+) - More aggressive</option>
            <option value={0.60}>Very Low (60%+) - Most aggressive</option>
          </select>
          {!settings.auto_apply_enabled && (
            <p className="text-xs text-amber-600 mt-2">
              Enable auto-apply to use this setting
            </p>
          )}
        </div>

        {/* Manual edit protection */}
        <div className="p-4 border border-border rounded-lg">
          <label className="font-medium text-brand-charcoal block mb-2">
            Protect manual edits
          </label>
          <p className="text-sm text-brand-slate mb-3">
            Items you've manually edited won't be automatically updated by AI for this period
          </p>
          <select
            value={settings.protect_manual_edits_days}
            onChange={(e) => updateSettings({ protect_manual_edits_days: parseInt(e.target.value) })}
            disabled={saving || !settings.auto_apply_enabled}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days (Recommended)</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          {!settings.auto_apply_enabled && (
            <p className="text-xs text-amber-600 mt-2">
              Enable auto-apply to use this setting
            </p>
          )}
        </div>

        {/* Info card */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">How wiki auto-update works</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• When you send a dispatch, AI extracts wiki-worthy information</li>
            <li>• High-confidence items are automatically added to your wiki</li>
            <li>• Lower-confidence items are queued for your review</li>
            <li>• You can always undo auto-applied changes from the wiki manager</li>
            <li>• Items you've manually edited are protected from AI changes</li>
          </ul>
        </div>
      </div>
    </Card>
  )
}

export default WikiSettingsPanel
